<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSEO } from '@/composables/useSEO';
import { useTranslationStore } from '@/stores/translation';
import { trackPurchase, markPurchaseOnce } from '@/analytics/track';
import axios from '../../axios';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const route = useRoute();
const translationStore = useTranslationStore();
const authStore = useAuthStore();

// Translation helper
const t = (label, modules) => {
  return translationStore.translate(label, modules);
};

// SEO Meta Tags with noindex (private page)
useSEO({
  title: computed(() => t('seo_title', ['order-success']) || 'Order Success - Chi Hun Da Su'),
  description: computed(() => t('seo_description', ['order-success']) || 'Your order has been placed successfully'),
  noindex: true, // Don't index order success pages
  path: "/order/success",
});

const isLoading = ref(true);
const paymentFailed = ref(false);
const orderNumber = ref(route.query.order_id || route.query.order || '');

// Promotional email capture on the success screen (opted in by default).
const promoEmail = ref('');
const promoConsent = ref(true);
const promoSaving = ref(false);
const promoSaved = ref(false);
const promoError = ref('');
const showPromo = computed(() => !!authStore.accessToken);
const savePromo = async () => {
  promoError.value = '';
  if (!promoEmail.value || !/^\S+@\S+\.\S+$/.test(promoEmail.value)) {
    promoError.value = 'Please enter a valid email address.';
    return;
  }
  promoSaving.value = true;
  try {
    await axios.post('/marketing-consent/', {
      email: promoEmail.value.trim(),
      marketing_consent: promoConsent.value,
    });
    promoSaved.value = true;
  } catch (e) {
    promoError.value = e.response?.data?.detail || 'Could not save — please try again.';
  } finally {
    promoSaving.value = false;
  }
};

// Function to check payment status from URL parameters
const checkPaymentStatus = async () => {
  try {
    // Check URL parameters that Stripe sends back
    const paymentIntent = route.query.payment_intent;
    // const paymentIntentClientSecret = route.query.payment_intent_client_secret;
    const paymentSuccess = route.query.redirect_status;
    
    // If we have payment parameters, the payment was processed
    if (paymentIntent) {
      // redirect_status: 'succeeded' means payment succeeded
      // redirect_status: 'failed' means payment failed
      if (paymentSuccess === 'failed') {
        paymentFailed.value = true;
        // Redirect back to checkout after a short delay
        setTimeout(() => {
          router.push({ name: 'CheckoutPage' });
        }, 3000);
      } else if (paymentSuccess === 'succeeded') {
        // Record the purchase to first-party analytics (revenue attribution) AND
        // forward it to every configured pixel (Meta/GA/TikTok) in one call.
        const orderValue = parseFloat(localStorage.getItem('lastOrderValue') || 0);
        const orderItems = parseInt(localStorage.getItem('lastOrderItems') || 0);
        const orderId = localStorage.getItem('lastOrderId') || paymentIntent || '';
        let orderLines = [];
        try { orderLines = JSON.parse(localStorage.getItem('lastOrderLines') || '[]'); } catch (e) { orderLines = []; }
        if (!orderId || markPurchaseOnce(orderId)) {
          trackPurchase({
            value: orderValue,
            orderId,
            eventId: orderId ? `purchase_${orderId}` : undefined,
            items: orderLines,
            numItems: orderItems || undefined,
            meta: { num_items: orderItems },
          });
        }
        localStorage.removeItem('lastOrderValue');
        localStorage.removeItem('lastOrderItems');
        localStorage.removeItem('lastOrderId');
        localStorage.removeItem('lastOrderLines');
      }
    }
    
    isLoading.value = false;
  } catch (error) {
    console.error('Error checking payment status:', error);
    isLoading.value = false;
  }
};

onMounted(() => {
  checkPaymentStatus();
  promoEmail.value = authStore.user?.email || '';
});
</script>

<template>
  <div class="order-success">
    <main class="main-content">
      <div class="success-container">
        <!-- Loading State -->
        <div v-if="isLoading" class="success-content">
          <div class="spinner-wrapper">
            <div class="spinner"></div>
          </div>
          <h1 class="title">Processing your payment...</h1>
        </div>
        
        <!-- Payment Failed State -->
        <div v-else-if="paymentFailed" class="success-content">
          <div class="icon-wrapper error">
            <svg class="error-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          
          <h1 class="title error">Payment Failed!</h1>
          
          <p class="order-number error">
            Unfortunately, your payment could not be processed. You will be redirected back to checkout shortly.
          </p>
          
          <div class="card">
            <div class="card-content">
              <div class="image-container">
                <img src="https://cdn-icons-png.flaticon.com/512/753/753345.png" 
                     alt="Payment Failed" />
              </div>
              
              <div class="info-section">
                <p class="confirmation-text">
                  Please check your payment details and try again.
                </p>
                
                <div class="button-container">
                  <router-link to="/checkout" class="continue-button error">
                    Back to Checkout
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Success State -->
        <div v-else class="success-content">
          <div class="icon-wrapper">
            <svg class="check-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h1 class="title">Order Placed Successfully!</h1>
          
          <p class="order-number">
            Thank you for your order.<template v-if="orderNumber"> Your order number is {{ orderNumber }}.</template> We've emailed your receipt and order details.
          </p>
          
          <div class="card">
            <div class="card-content">
              <div class="image-container">
                <img src="https://cdn-icons-png.flaticon.com/512/4108/4108921.png" 
                     alt="Order Success" />
              </div>
              
              <div class="info-section">
                <p class="confirmation-text">
                  We'll send you a confirmation email with your order details and tracking information.
                </p>
                
                <div class="button-container">
                  <router-link to="/" class="continue-button">
                    Continue Shopping
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <!-- Promotional email capture (opted in by default) -->
          <div v-if="showPromo && !promoSaved" class="promo-card">
            <h2 class="promo-title">Stay in the loop 🍱</h2>
            <p class="promo-sub">Get exclusive offers, new dishes and members-only discounts.</p>
            <input v-model="promoEmail" type="email" class="promo-input" placeholder="you@email.com" />
            <label class="promo-check">
              <input v-model="promoConsent" type="checkbox" />
              <span>Yes, email me CHDS offers &amp; updates</span>
            </label>
            <p v-if="promoError" class="promo-error">{{ promoError }}</p>
            <button class="promo-btn" :disabled="promoSaving" @click="savePromo">
              {{ promoSaving ? 'Saving…' : 'Keep me updated' }}
            </button>
          </div>
          <div v-else-if="showPromo && promoSaved" class="promo-card saved">
            <p class="promo-thanks">✅ You’re on the list — thanks!</p>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<style scoped>
.order-success {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex-grow: 1;
  padding: 2rem 1rem;
}

.success-container {
  max-width: 600px;
  margin: 0 auto;
  padding-top: 2rem;
}

.success-content {
  text-align: center;
}

.icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  background-color: #ecfdf5;
  border-radius: 50%;
  margin-bottom: 1.5rem;
  box-shadow: 0 0 15px rgba(52, 211, 153, 0.3);
  animation: bounce-in 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.check-icon {
  width: 1.5rem;
  height: 1.5rem;
  stroke: #059669;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.title {
  font-size: 2rem;
  font-weight: 800;
  margin: 1rem 0;
  background: linear-gradient(120deg, #4f46e5, #34d399);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: slide-up 0.6s ease-out;
}

.order-number {
  font-size: 1.125rem;
  color: #4b5563;
  margin-bottom: 2rem;
  animation: fade-in 0.8s ease-out;
}

.card {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: scale-in 0.7s ease-out;
}

.card-content {
  padding: 1.5rem;
}

.image-container {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.image-container img {
  width: 120px;
  height: 120px;
  object-fit: contain;
  animation: float 3s ease-in-out infinite;
}

.info-section {
  border-top: 1px solid #e5e7eb;
  padding-top: 1.5rem;
}

.confirmation-text {
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.button-container {
  display: flex;
  justify-content: center;
}

.continue-button {
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  background-color: #4f46e5;
  color: white;
  font-weight: 500;
  border-radius: 0.375rem;
  transition: all 0.3s ease;
  text-decoration: none;
}

.continue-button:hover {
  background-color: #4338ca;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2);
}

.continue-button:active {
  transform: translateY(0);
}

@media (min-width: 640px) {
  .main-content {
    padding: 3rem 2rem;
  }

  .title {
    font-size: 2.5rem;
  }

  .image-container img {
    width: 150px;
    height: 150px;
  }
}

@keyframes bounce-in {
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes slide-up {
  0% { transform: translateY(20px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}

@keyframes fade-in {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

@keyframes scale-in {
  0% { transform: scaleY(0.95); opacity: 0; }
  100% { transform: scaleY(1); opacity: 1; }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

/* Error state styles */
.icon-wrapper.error {
  background-color: #fef2f2;
  box-shadow: 0 0 15px rgba(239, 68, 68, 0.3);
}

.error-icon {
  width: 1.5rem;
  height: 1.5rem;
  stroke: #dc2626;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.title.error {
  background: linear-gradient(120deg, #dc2626, #ef4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.order-number.error {
  color: #dc2626;
}

.continue-button.error {
  background-color: #dc2626;
}

.continue-button.error:hover {
  background-color: #b91c1c;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
}

/* Loading spinner styles */
.spinner-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  height: 3rem;
}

.spinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #4f46e5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Promotional email capture card */
.promo-card {
  margin-top: 1.5rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.5rem;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.06);
  animation: scale-in 0.7s ease-out;
}
.promo-title { font-size: 1.15rem; font-weight: 800; color: #446936; margin: 0 0 0.35rem; -webkit-text-fill-color: initial; }
.promo-sub { color: #6b7280; font-size: 0.95rem; margin: 0 0 1rem; }
.promo-input {
  width: 100%; box-sizing: border-box; padding: 0.7rem 0.9rem;
  border: 1px solid #d1d5db; border-radius: 0.5rem; font-size: 1rem; margin-bottom: 0.75rem;
}
.promo-input:focus { outline: none; border-color: #B6D452; box-shadow: 0 0 0 3px rgba(182, 212, 82, 0.35); }
.promo-check {
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  font-size: 0.9rem; color: #4b5563; margin-bottom: 1rem; cursor: pointer;
}
.promo-check input { width: 1.05rem; height: 1.05rem; accent-color: #446936; }
.promo-error { color: #dc2626; font-size: 0.85rem; margin: 0 0 0.75rem; }
.promo-btn {
  width: 100%; padding: 0.75rem 1rem; background: #446936; color: #fff;
  font-weight: 700; border: none; border-radius: 0.5rem; cursor: pointer;
  transition: background 0.2s ease;
}
.promo-btn:hover:not(:disabled) { background: #375528; }
.promo-btn:disabled { opacity: 0.6; cursor: default; }
.promo-thanks { color: #446936; font-weight: 700; margin: 0; }
</style>